import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';

import { Utility } from './../../Common/Services/utility';

/** How to Use
 * pass formControlName and isDisable property with component.
 * Syntex:  <custom-time-control formControlName="examLength" [isDisable]="disableField"></custom-time-control>
 */
@Component({
  selector: 'custom-time-control',
  templateUrl: './custom-time-control.component.html',
  styleUrls: ['./custom-time-control.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomTimeControlComponent),
      multi: true
    }
  ]
})
export class CustomTimeControlComponent implements ControlValueAccessor, OnChanges {
  form: FormGroup;
  control: any;
  dateFormatOptions: any = ['AM', 'PM'];
  configModel = {
    hhMin: 0,
    hhMax: 23,
    hhShow: true,
    mmMin: 0,
    mmMax: 59,
    mmShow: true,
    ssMin: 0,
    ssMax: 59,
    ssShow: false,
    format: 24 // for 24 hours pass 24 & for 12 hours format pass 12 
  };

  @Input('value') _value;
  @Input('isDisable') isControlDisable: boolean = false;
  @Input('defaultTime') defaultTime: string = "0:0";
  @Input('showTimerIcon') showTimerIcon: boolean = true;

  @Output()
  change: EventEmitter<number> = new EventEmitter<number>();   // In Case you have a need to add change event in your parent Component you can use it.

  onChange: any = () => { };
  onTouched: any = () => { };

  constructor(private _utility: Utility) {
    if (this.configModel.format == 12) {
      this.configModel.hhMax = 11;
    }
  }

  /**
   * 
   * @param changes - If property binding changes occurred, this method will be called
   */
  ngOnChanges(changes: SimpleChanges) {
    let value = changes.isControlDisable ? changes.isControlDisable.currentValue : '';
    if (this.form) {
      if (value) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }

  ngOnInit() {
    this.form = new FormGroup({
      hh: new FormControl('0', [Validators.required, Validators.min(this.configModel.hhMin), Validators.max(this.configModel.hhMax), Validators.maxLength(this.configModel.hhMax.toString().length)]),
      mm: new FormControl('0', [Validators.required, Validators.min(this.configModel.mmMin), Validators.max(this.configModel.mmMax), Validators.maxLength(this.configModel.mmMax.toString().length)]),
      ss: new FormControl('0', [Validators.required, Validators.min(this.configModel.ssMin), Validators.max(this.configModel.ssMax), Validators.maxLength(this.configModel.ssMax.toString().length)]),
      format: new FormControl('')
    });
    this.control = this.form.controls;

    this.form.valueChanges
      .debounceTime(50)
      .distinctUntilChanged()
      .subscribe(form => {
        this.onValueChange();
      });
    if (this.isControlDisable) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }


  get value() {
    return this._value;
  }

  set value(val) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }


  registerOnChange(fn) {
    this.onChange = fn;
  }

  /**
   * Write value method
   * If format is selected 12 hours then convert incoming-time into 12 hours 
   * @param value Write value on field when it comes in control
   */
  writeValue(value) {
    if (!value) value = this.defaultTime;
    if (value) {
      this.value = value;
      let arr = [value];
      if (this.configModel.format == 12) {
        value = this.convertTimeIn12hrs(value);
        arr = value.split(' ');
        this.control.format.setValue(arr[1]);  // set AM, PM
      }
      let arrTime: any = arr[0].split(':');

      this.control.hh.setValue((arrTime.length > 0 && arrTime[0] != "" && Number(arrTime[0]) < 10) ? arrTime[0].slice(-1) : (arrTime[0] != "") ? arrTime[0] : 0);
      this.control.mm.setValue((arrTime.length > 1 && arrTime[1] != "" && Number(arrTime[1]) < 10) ? arrTime[1].slice(-1) : (arrTime[1] != "") ? arrTime[1] : 0);
      this.control.ss.setValue((arrTime.length > 2 && arrTime[2] != "" && Number(arrTime[2]) < 10) ? arrTime[2].slice(-1) : (arrTime[2] != "") ? arrTime[2] : 0);

    }
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }
  onValueChange() {
    this.value = this.getResult();
    this.change.emit(this.value);
  }

  onBlur(field) {
    this.checkForValid();

  }

  /**
   * It returns hours and minutes after append it by colon (:)
   */
  getResult(): string {
    this.checkForValid();
    let values = this.form.getRawValue();
    let tempValue = '';

    if (this.configModel.hhShow && values.hh != null) {
      tempValue = ('0' + values.hh).slice(-2);
    }
    if (this.configModel.mmShow && values.mm != null) {
      tempValue += (tempValue == '' ? ('0' + values.mm).slice(-2) : ':' + ('0' + values.mm).slice(-2));
    }
    if (this.configModel.ssShow && values.ss != null) {
      tempValue += (tempValue == '' ? ('0' + values.ss).slice(-2) : ':' + ('0' + values.ss).slice(-2));
    }
    if (this.configModel.format == 12) {
      tempValue += ` ${values.format}`;
    }

    if (tempValue == '') {
      tempValue = "00:00";
    }
    if (this.configModel.format == 12) { tempValue = this.convertTimeIn24hrs(tempValue); }
    return tempValue;
  }

  /**
   * Check for hours, minutes and seconds invalid.
   * If there are invalid for min value or max value , then set default value 
   * IF field is blank with no data then set 0 into that field
   */
  private checkForValid() {
    let hours = this.control.hh;
    if (hours.invalid) {
      let temphh = (hours.errors.min) ? this.configModel.hhMin : hours.errors.max ? this.configModel.hhMax : '0';
      hours.setValue(temphh);
    } else {
      hours.setValue(hours.value);
    }
    let minutes = this.control.mm;
    if (minutes.invalid) {
      let tempmm = (minutes.errors.min) ? this.configModel.mmMin : minutes.errors.max ? this.configModel.mmMax : '0';
      minutes.setValue(tempmm);
    } else {
      minutes.setValue(minutes.value);
    }
    let seconds = this.control.ss;
    if (seconds.invalid) {
      let tempss = (seconds.errors.min) ? this.configModel.ssMin : seconds.errors.max ? this.configModel.ssMax : '0';
      seconds.setValue(tempss);
    }

  }

  private convertTimeIn24hrs(time: string) {
    let hours = Number(time.match(/^(\d+)/)[1]);
    let minutes = Number(time.match(/:(\d+)/)[1]);
    let finalTime = time;
    if (time.match(/\s(.*)$/)) {
      let AMPM = time.match(/\s(.*)$/)[1];
      if (AMPM == "PM" && hours < 12) hours = hours + 12;
      if (AMPM == "AM" && hours == 12) hours = hours - 12;
      finalTime = hours.toString() + ":" + minutes.toString();
    }
    return finalTime;
  }

  private convertTimeIn12hrs(time: any) {
    // Check correct time format and split into components
    time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];

    if (time.length > 1) { // If time format correct
      time = time.slice(1);  // Remove full string match value
      time[5] = +time[0] < 12 ? ' AM' : ' PM'; // Set AM/PM
      time[0] = +time[0] % 12 || 12; // Adjust hours
    }
    return time.join(''); // return adjusted time or original string
  }
}



// WEBPACK FOOTER //
// /opt/app-root/src/src/app/Components/custom-time-control/custom-time-control.component.ts